import {
  DELETE_USER,
  ON_AUTHSTATE_FAIL,
  ON_AUTHSTATE_SUCCESS,
  RESET_PASSWORD,
  SET_AUTH_PERSISTENCE,
  SIGNIN,
  SIGNIN_WITH_FACEBOOK,
  SIGNIN_WITH_GOOGLE,
  SIGNOUT,
  SIGNUP
} from 'constants/constants';
import { SIGNIN as ROUTE_SIGNIN } from 'constants/routes';
import defaultAvatar from 'images/defaultAvatar.png';
import defaultBanner from 'images/defaultBanner.jpg';
import { call, put } from 'redux-saga/effects';
import { signInSuccess, signOutSuccess } from 'redux/actions/authActions';
import { resetFilter } from 'redux/actions/filterActions';
import { setAuthenticating, setAuthStatus } from 'redux/actions/miscActions';
import { clearProfile, setProfile } from 'redux/actions/profileActions';
import { history } from 'routers/AppRouter';
import firebase from 'firebase/firebase';
import { clearOwnProducts } from 'redux/actions/productActions';

function* handleError(e) {
  console.error(e);
  const obj = { success: false, type: 'auth', isError: true };
  yield put(setAuthenticating(false));

  switch (e.code) {
    case 'auth/network-request-failed':
      yield put(setAuthStatus({ ...obj, message: 'errorNetwork' }));
      break;
    case 'auth/email-already-in-use':
      yield put(
        setAuthStatus({
          ...obj,
          message: 'errorEmailAlreadyUse'
        })
      );
      break;
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      yield put(setAuthStatus({ ...obj, message: 'errorInvalidCredentials' }));
      break;
    case 'auth/reset-password-error':
      yield put(
        setAuthStatus({
          ...obj,
          message: 'errorPasswordReset'
        })
      );
      break;
    case 'auth/account-exists-with-different-credential':
      yield put(
        setAuthStatus({
          ...obj,
          message: 'errorAccountExists'
        })
      );
      break;
    default:
      yield put(setAuthStatus({ ...obj, message: e.message }));
      break;
  }
}

function* initRequest() {
  yield put(setAuthenticating());
  yield put(setAuthStatus({}));
}

function* authSaga({ type, payload }) {
  switch (type) {
    case SIGNIN:
      try {
        yield initRequest();
        yield call(firebase.signIn, payload.email, payload.password);
      } catch (e) {
        yield handleError(e);
      }
      break;
    case SIGNIN_WITH_GOOGLE:
      try {
        yield initRequest();
        yield call(firebase.signInWithGoogle);
      } catch (e) {
        yield handleError(e);
      }
      break;
    case SIGNIN_WITH_FACEBOOK:
      try {
        yield initRequest();
        yield call(firebase.signInWithFacebook);
      } catch (e) {
        yield handleError(e);
      }
      break;
    case SIGNUP:
      try {
        yield initRequest();
        const ref = yield call(
          firebase.createAccount,
          payload.email,
          payload.password
        );
        const fullname = payload.fullname
          .split(' ')
          .map((name) => name[0].toUpperCase().concat(name.substring(1)))
          .join(' ');
        const user = {
          fullname,
          avatar: defaultAvatar,
          banner: defaultBanner,
          email: payload.email,
          address: { data: {} },
          mobile: { data: {} },
          role: payload.iva ? 'COMPANY' : 'USER',
          blocked: 'false',
          dateJoined: ref.user.metadata.creationTime || new Date().getTime()
        };
        if (user.role === 'COMPANY') {
          user.iva = payload.iva;
          user.representativeCode = payload.representativeCode;
        }
        yield call(firebase.addUser, ref.user.uid, user, payload.password);
        yield put(setProfile(user));
        yield put(setAuthenticating(false));
      } catch (e) {
        console.error(e);
        yield handleError(e);
      }
      break;
    case SIGNOUT: {
      try {
        yield initRequest();
        yield call(history.push, ROUTE_SIGNIN);
        yield put(clearProfile());
        yield put(clearOwnProducts());
        yield put(resetFilter());
        yield put(signOutSuccess());
        yield put(setAuthenticating(false));
        yield call(firebase.signOut);
      } catch (e) {
        console.error(e);
      }
      break;
    }
    case RESET_PASSWORD: {
      try {
        yield initRequest();
        yield call(firebase.passwordReset, payload);
        yield put(
          setAuthStatus({
            success: true,
            type: 'reset',
            message: 'successEmailResetPassword'
          })
        );
        yield put(setAuthenticating(false));
      } catch (e) {
        yield handleError({ code: 'auth/reset-password-error' });
      }
      break;
    }
    case ON_AUTHSTATE_SUCCESS: {
      const snapshot = yield call(firebase.getUser, payload.uid);

      if (snapshot.data()) {
        const user = snapshot.data();

        yield put(setProfile(user));
        yield put(
          signInSuccess({
            id: payload.uid,
            role: user.role,
            provider: payload.providerData[0].providerId
          })
        );
      }

      yield put(
        setAuthStatus({
          success: true,
          type: 'auth',
          isError: false,
          message: 'loadingMessage'
        })
      );
      yield put(setAuthenticating(false));
      break;
    }
    case ON_AUTHSTATE_FAIL: {
      yield put(clearProfile());
      yield put(signOutSuccess());
      break;
    }
    case SET_AUTH_PERSISTENCE: {
      try {
        yield call(firebase.setAuthPersistence);
      } catch (e) {
        console.error(e);
      }
      break;
    }
    case DELETE_USER: {
      try {
        yield call(firebase.deleteUser, payload.password);
        window.location.reload(true);
      } catch (e) {
        console.error(e);
      }
      break;
    }
    default: {
      throw new Error(`Unexpected action type ${type}`);
    }
  }
}

export default authSaga;
